import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  DollarSign, Save, Loader2, AlertTriangle, Package, 
  TrendingUp, Users, Edit, Trash2, Plus, CreditCard
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreditsManagement = ({ token }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [usageStats, setUsageStats] = useState(null);
  const [editingPackage, setEditingPackage] = useState(null);
  const [newPackage, setNewPackage] = useState({ 
    id: '', 
    credits: 0, 
    price_eur: 0, 
    name: '', 
    discount_percent: 0, 
    popular: false 
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [configRes, statsRes] = await Promise.all([
        axios.get(`${API}/credits/admin/config`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/credits/admin/usage-stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setConfig(configRes.data);
      setUsageStats(statsRes.data);
    } catch (error) {
      toast.error('Error cargando configuración de créditos');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/credits/admin/config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error guardando configuración');
    } finally {
      setSaving(false);
    }
  };

  const updateActionCost = (action, credits) => {
    setConfig(prev => ({
      ...prev,
      action_costs: {
        ...prev.action_costs,
        [action]: { ...prev.action_costs[action], credits: parseInt(credits) || 0 }
      }
    }));
  };

  const updatePlanCredits = (plan, monthly) => {
    setConfig(prev => ({
      ...prev,
      plan_credits: {
        ...prev.plan_credits,
        [plan]: { ...prev.plan_credits[plan], monthly: parseInt(monthly) || 0 }
      }
    }));
  };

  const addPackage = () => {
    if (!newPackage.id || !newPackage.credits || !newPackage.price_eur) {
      toast.error('Completa todos los campos del paquete');
      return;
    }
    const packageToAdd = {
      ...newPackage,
      discount: newPackage.discount_percent ? `${newPackage.discount_percent}%` : null,
      discount_percent: newPackage.discount_percent || null,
      popular: newPackage.popular || false,
      active: true
    };
    setConfig(prev => ({
      ...prev,
      purchase_packages: [...(prev.purchase_packages || []), packageToAdd]
    }));
    setNewPackage({ id: '', credits: 0, price_eur: 0, name: '', discount_percent: 0, popular: false });
    toast.success('Paquete añadido');
  };

  const removePackage = (idx) => {
    setConfig(prev => ({
      ...prev,
      purchase_packages: prev.purchase_packages.filter((_, i) => i !== idx)
    }));
    toast.success('Paquete eliminado');
  };

  const updatePackage = (idx, field, value) => {
    setConfig(prev => {
      const packages = [...prev.purchase_packages];
      packages[idx] = { ...packages[idx], [field]: value };
      if (field === 'discount_percent') {
        packages[idx].discount = value > 0 ? `${value}%` : null;
      }
      return { ...prev, purchase_packages: packages };
    });
  };

  const toggleEditPackage = (idx) => {
    setEditingPackage(editingPackage === idx ? null : idx);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Usage Stats Overview */}
      {usageStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5" />
              <span className="text-sm opacity-90">Créditos Consumidos</span>
            </div>
            <p className="text-2xl font-bold">{usageStats.overview?.total_credits_consumed?.toLocaleString() || 0}</p>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5" />
              <span className="text-sm opacity-90">Coste Total</span>
            </div>
            <p className="text-2xl font-bold">{usageStats.overview?.total_cost_eur?.toFixed(2) || '0.00'}€</p>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5" />
              <span className="text-sm opacity-90">Usuarios Totales</span>
            </div>
            <p className="text-2xl font-bold">{usageStats.overview?.total_users || 0}</p>
          </div>
          <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5" />
              <span className="text-sm opacity-90">Media por Usuario</span>
            </div>
            <p className="text-2xl font-bold">{usageStats.overview?.avg_credits_per_user?.toFixed(0) || 0}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost Configuration */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-600" />
            Coste Interno por Crédito
          </h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-600">Coste por crédito (€)</label>
              <Input
                type="number"
                step="0.0001"
                value={config?.cost_per_credit_eur || 0}
                onChange={(e) => setConfig({ ...config, cost_per_credit_eur: parseFloat(e.target.value) })}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">1000 créditos = {((config?.cost_per_credit_eur || 0) * 1000).toFixed(2)}€</p>
            </div>
            <div>
              <label className="text-sm text-slate-600">Máximo créditos/alumno (control de coste)</label>
              <Input
                type="number"
                value={config?.max_credits_per_student || 0}
                onChange={(e) => setConfig({ ...config, max_credits_per_student: parseInt(e.target.value) })}
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-600">Alerta créditos bajos</label>
                <Input
                  type="number"
                  value={config?.low_credits_threshold || 0}
                  onChange={(e) => setConfig({ ...config, low_credits_threshold: parseInt(e.target.value) })}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm text-slate-600">Alerta crítica</label>
                <Input
                  type="number"
                  value={config?.critical_credits_threshold || 0}
                  onChange={(e) => setConfig({ ...config, critical_credits_threshold: parseInt(e.target.value) })}
                  className="mt-1"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Costs */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Coste por Acción (Créditos)</h3>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {config?.action_costs && Object.entries(config.action_costs).map(([action, data]) => (
              <div key={action} className="flex items-center justify-between gap-4 p-2 bg-slate-50 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-700">{data.description || action}</p>
                  <p className="text-xs text-slate-500">{action}</p>
                </div>
                <Input
                  type="number"
                  value={data.credits}
                  onChange={(e) => updateActionCost(action, e.target.value)}
                  className="w-24"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Plan Credits */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Créditos Mensuales por Plan</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {config?.plan_credits && Object.entries(config.plan_credits).map(([plan, data]) => (
            <div key={plan} className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm font-medium text-slate-700 capitalize mb-2">{plan}</p>
              <Input
                type="number"
                value={data.monthly}
                onChange={(e) => updatePlanCredits(plan, e.target.value)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Purchase Packages */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Package className="w-5 h-5 text-indigo-600" />
          Paquetes de Compra de Créditos
        </h3>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left p-2 text-sm text-slate-600">ID</th>
                <th className="text-left p-2 text-sm text-slate-600">Nombre</th>
                <th className="text-left p-2 text-sm text-slate-600">Créditos</th>
                <th className="text-left p-2 text-sm text-slate-600">Precio (€)</th>
                <th className="text-left p-2 text-sm text-slate-600">Descuento %</th>
                <th className="text-left p-2 text-sm text-slate-600">Popular</th>
                <th className="p-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {config?.purchase_packages?.map((pkg, idx) => (
                <tr key={idx} className="border-b border-slate-100">
                  {editingPackage === idx ? (
                    <>
                      <td className="p-2">
                        <Input
                          value={pkg.id}
                          onChange={(e) => updatePackage(idx, 'id', e.target.value)}
                          className="w-24"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={pkg.name}
                          onChange={(e) => updatePackage(idx, 'name', e.target.value)}
                          className="w-32"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          value={pkg.credits}
                          onChange={(e) => updatePackage(idx, 'credits', parseInt(e.target.value))}
                          className="w-24"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          step="0.01"
                          value={pkg.price_eur}
                          onChange={(e) => updatePackage(idx, 'price_eur', parseFloat(e.target.value))}
                          className="w-20"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          value={pkg.discount_percent || 0}
                          onChange={(e) => updatePackage(idx, 'discount_percent', parseInt(e.target.value))}
                          className="w-16"
                          placeholder="0"
                        />
                      </td>
                      <td className="p-2">
                        <input
                          type="checkbox"
                          checked={pkg.popular || false}
                          onChange={(e) => updatePackage(idx, 'popular', e.target.checked)}
                          className="rounded border-slate-300"
                        />
                      </td>
                      <td className="p-2 flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => toggleEditPackage(idx)}>
                          <Save className="w-4 h-4 text-green-500" />
                        </Button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="p-2 text-sm">{pkg.id}</td>
                      <td className="p-2 text-sm">{pkg.name}</td>
                      <td className="p-2 text-sm font-medium">{pkg.credits?.toLocaleString()}</td>
                      <td className="p-2 text-sm">{pkg.price_eur}€</td>
                      <td className="p-2 text-sm">
                        {pkg.discount_percent ? (
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs">
                            {pkg.discount_percent}% OFF
                          </span>
                        ) : '-'}
                      </td>
                      <td className="p-2 text-sm">
                        {pkg.popular && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs">
                            ⭐ Popular
                          </span>
                        )}
                      </td>
                      <td className="p-2 flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => toggleEditPackage(idx)}>
                          <Edit className="w-4 h-4 text-blue-500" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => removePackage(idx)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Add new package */}
        <div className="mt-4 p-4 bg-slate-50 rounded-lg">
          <p className="text-sm font-medium text-slate-700 mb-3">Añadir Nuevo Paquete</p>
          <div className="grid grid-cols-2 md:grid-cols-7 gap-3 items-end">
            <div>
              <label className="text-xs text-slate-500">ID</label>
              <Input
                placeholder="pack_xxx"
                value={newPackage.id}
                onChange={(e) => setNewPackage({ ...newPackage, id: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs text-slate-500">Nombre</label>
              <Input
                placeholder="Nombre"
                value={newPackage.name}
                onChange={(e) => setNewPackage({ ...newPackage, name: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs text-slate-500">Créditos</label>
              <Input
                type="number"
                placeholder="Créditos"
                value={newPackage.credits || ''}
                onChange={(e) => setNewPackage({ ...newPackage, credits: parseInt(e.target.value) })}
              />
            </div>
            <div>
              <label className="text-xs text-slate-500">Precio €</label>
              <Input
                type="number"
                step="0.01"
                placeholder="Precio €"
                value={newPackage.price_eur || ''}
                onChange={(e) => setNewPackage({ ...newPackage, price_eur: parseFloat(e.target.value) })}
              />
            </div>
            <div>
              <label className="text-xs text-slate-500">Descuento %</label>
              <Input
                type="number"
                placeholder="0"
                value={newPackage.discount_percent || ''}
                onChange={(e) => setNewPackage({ ...newPackage, discount_percent: parseInt(e.target.value) })}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="popular-new"
                checked={newPackage.popular || false}
                onChange={(e) => setNewPackage({ ...newPackage, popular: e.target.checked })}
                className="rounded border-slate-300"
              />
              <label htmlFor="popular-new" className="text-xs text-slate-500">Popular</label>
            </div>
            <Button onClick={addPackage} className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4 mr-1" /> Añadir
            </Button>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={saveConfig} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Guardar Configuración
        </Button>
      </div>
    </div>
  );
};

export default CreditsManagement;
